import { isValidEmail } from '../Inputs/email-validation';

/** One address in a recipient field: the address and, when known, the name. */
export interface NxRecipient {
  email: string;
  name?: string;
}

/** A hit of the caller's directory lookup — a recipient plus what the row shows beside it. */
export interface NxRecipientSuggestion extends NxRecipient {
  /** A directory group expands into these recipients when selected. */
  members?: readonly NxRecipient[];
  /** Quiet text at the end of the row: the address book, the department. */
  sublabel?: string;
}

/**
 * The caller's directory lookup. May answer synchronously or with a promise;
 * `signal` aborts when a newer query takes over or the field is left.
 */
export type NxRecipientSearch = (
  query: string,
  signal: AbortSignal
) => readonly NxRecipientSuggestion[] | PromiseLike<readonly NxRecipientSuggestion[]>;

/** Characters that end an address while typing (`,` `;`) or in pasted text (also line breaks). */
const SEPARATORS = new Set([',', ';', '\n', '\r']);

/** Identity of a recipient: the address, case-insensitive. */
export function recipientKey(recipient: Pick<NxRecipient, 'email'>): string {
  return recipient.email.trim().toLowerCase();
}

/** „Name <addr@x.de>“, or the bare address without a name. */
export function formatRecipient(recipient: NxRecipient): string {
  const name = recipient.name?.trim();
  if (!name) return recipient.email;
  const quoted = /[",;<>@]/.test(name) ? `"${name.replace(/"/g, '')}"` : name;
  return `${quoted} <${recipient.email}>`;
}

/** The chip text: the name when there is one, else the address. */
export function recipientLabel(recipient: NxRecipient): string {
  return recipient.name?.trim() || recipient.email;
}

/**
 * Split typed or pasted text at `,` `;` and line breaks — but not inside a
 * quoted name or an `<address>`, so `"Doe, Jane" <jane@x.de>` stays whole.
 */
export function splitRecipientText(text: string): string[] {
  const parts: string[] = [];
  let start = 0;
  for (const at of separatorPositions(text)) {
    parts.push(text.slice(start, at));
    start = at + 1;
  }
  parts.push(text.slice(start));
  return parts.map((part) => part.trim()).filter(Boolean);
}

/** Positions of the separators that end an address (outside quotes and `<…>`). */
function separatorPositions(text: string): number[] {
  const positions: number[] = [];
  let quoted = false;
  let angled = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i]!;
    if (char === '"') quoted = !quoted;
    else if (char === '<' && !quoted) angled = true;
    else if (char === '>' && !quoted) angled = false;
    else if (SEPARATORS.has(char) && !quoted && !angled) positions.push(i);
  }
  return positions;
}

/**
 * One token → recipients. Reads `Name <addr>`, `"Name" <addr>`, `<addr>` and a
 * bare address; a token of several whitespace-separated addresses
 * („a@x.de b@x.de“, pasted from a list) gives one recipient each. Anything
 * else is kept as typed — the field marks it invalid rather than dropping it.
 */
export function parseRecipient(token: string): NxRecipient[] {
  const text = token.trim();
  if (!text) return [];
  const angle = /^(.*?)<([^<>]*)>\s*$/.exec(text);
  if (angle) {
    const email = angle[2]!.trim();
    const name = angle[1]!
      .trim()
      .replace(/^"(.*)"$/, '$1')
      .trim();
    if (!email) return name ? [{ email: name }] : [];
    return [name ? { email, name } : { email }];
  }
  const words = text.split(/\s+/);
  if (words.length > 1 && words.every((word) => word.includes('@'))) {
    return words.map((email) => ({ email }));
  }
  return [{ email: text }];
}

/** All recipients in a piece of typed or pasted text. */
export function parseRecipients(text: string): NxRecipient[] {
  return splitRecipientText(text).flatMap(parseRecipient);
}

/**
 * Typed text → the addresses it completes and the fragment still being typed.
 * Only text before the last separator is complete: „a@x.de, b@“ gives
 * `a@x.de` and keeps „b@“ in the input.
 */
export function takeCompleteRecipients(text: string): { complete: NxRecipient[]; rest: string } {
  const last = separatorPositions(text).at(-1);
  if (last === undefined) return { complete: [], rest: text.trimStart() };
  return {
    complete: parseRecipients(text.slice(0, last)),
    rest: text.slice(last + 1).trimStart(),
  };
}

/** Append without duplicates (by address); a later name fills an earlier nameless entry. */
export function addRecipients(
  list: readonly NxRecipient[],
  additions: readonly NxRecipient[]
): NxRecipient[] {
  const next = [...list];
  for (const addition of additions) {
    const email = addition.email.trim();
    if (!email) continue;
    const key = recipientKey(addition);
    const at = next.findIndex((entry) => recipientKey(entry) === key);
    if (at < 0) {
      next.push(addition.name?.trim() ? { email, name: addition.name.trim() } : { email });
    } else if (!next[at]!.name && addition.name?.trim()) {
      next[at] = { ...next[at]!, name: addition.name.trim() };
    }
  }
  return next.length === list.length && next.every((entry, i) => entry === list[i])
    ? (list as NxRecipient[])
    : next;
}

/** The default address rule: the kit's e-mail check, and never empty. */
export function isValidRecipientEmail(email: string): boolean {
  return email.trim().length > 0 && isValidEmail(email.trim());
}
